import React, { useEffect, useState } from "react";
import {
    changeStatusBlockClient,
    changeStatusClient,
    deleteClient,
    getClients,
    getClientsByTherapistId,
} from "../../../../api/client";
import { alertError, alertSuccess } from "../../../../utils/logger";
import { shallowEqual, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import Table, { buttonsStyle, dateFormatter } from "../../../components/tables/table";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, FormLabel, MenuItem, Radio, RadioGroup, TextField, Tooltip } from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import EventIcon from "@material-ui/icons/Event";
import ToggleOffIcon from "@material-ui/icons/ToggleOff";
import ToggleOnIcon from "@material-ui/icons/ToggleOn";
import DeleteIcon from "@material-ui/icons/Delete";
import { Card, CardBody, CardHeader, CardHeaderToolbar } from "../../../../_metronic/_partials/controls";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import { Visibility } from "@material-ui/icons";
import {
    canManageAppointments,
    getClientGroupTherapists,
    getTherapistForBlock,
    isOnlyTherapist,
    therapistLabel,
} from "../../../../_metronic/_assets/js/components/user.utils";
import { getTherapeuticGroupsByTherapist } from "../../../../api/therapeuticGroup";
import AppointmentDialog from "./AppointmentDialog";
import AvailabilityGrid from "./AvailabilityGrid";

const BLOCK_FIELD_MAP = {
    block1Done: "block1",
    block2Done: "block2",
    block3Done: "block3",
};

function getTherapistId(therapistRef) {
    if (!therapistRef) return null;
    return therapistRef._id || therapistRef;
}

function getBlockAcronym(elem, block) {
    const blockKey = BLOCK_FIELD_MAP[block];
    if (!blockKey) return null;
    const therapist = elem?.therapeuticGroup?.[blockKey]?.therapist;
    if (!therapist || typeof therapist !== "object") return null;
    return therapist.acronym || null;
}

function splitClientsByFinalized(list) {
    const active = [];
    const archived = [];
    for (let i = 0; i < (list?.length || 0); ++i) {
        const actC = list[i];
        if (!actC?.finalized) active.push(actC);
        else archived.push(actC);
    }
    return { active, archived };
}

export default function ClientPage() {
    const history = useHistory();
    const user = useSelector((store) => store.authentication?.user, shallowEqual);
    const [clients, setClients] = useState([]);
    const [oldClients, setOldClients] = useState([]);
    const [client, setClient] = useState(null);
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [block, setBlock] = useState("");
    const [blockNote, setBlockNote] = useState("");
    const [refresh, setRefresh] = useState(false);
    const [appointmentClient, setAppointmentClient] = useState(null);
    const [nextAppointmentAction, setNextAppointmentAction] = useState("fix");
    const [appointmentTherapistId, setAppointmentTherapistId] = useState("");
    const [appointmentStart, setAppointmentStart] = useState("");
    const [appointmentDuration, setAppointmentDuration] = useState(60);

    const [therapeuticGroups, setTherapeuticGroups] = useState([]);
    const readOnly = (!user?.isTherapist);

    useEffect(() => {
        setRefresh(false);
    }, [refresh])

    useEffect(() => {
        fetch();
    }, [])

    function fetch() {
        if (user?.isTherapist) {
            getTherapeuticGroupsByTherapist(user?._id)
                .then((res) => {
                    setTherapeuticGroups(res.data);
                })
                .catch((error) => {
                    if (error.request.status !== 404) {
                        alertError({
                            error,
                            customMessage: "Error al buscar los grupos terapéuticos."
                        })
                    }
                })
        }

        if (!readOnly) {
            getClientsByTherapistId(user?._id)
                .then((res) => {
                    const { active, archived } = splitClientsByFinalized(res?.data);
                    setClients(active);
                    setOldClients(archived);
                })
                .catch((error) => {
                    if (error.request.status !== 404) {
                        alertError({
                            error,
                            customMessage: "No se ha podido encontrar los clientes."
                        })
                    }
                })
        }
        else {
            getClients()
                .then((res) => {
                    const { active, archived } = splitClientsByFinalized(res?.data);
                    setClients(active);
                    setOldClients(archived);
                })
                .catch((error) => {
                    if (error.request.status !== 404) {
                        alertError({
                            error,
                            customMessage: "No se ha podido encontrar los clientes."
                        })
                    }
                })
        }
    }

    const columns = [
        { dataField: "fullName", text: "Nombre", sort: true,
            headerStyle: { width: '120px' } },
        { dataField: "surname", text: "Apellidos", sort: true,
            headerStyle: { width: '120px' } },
        { dataField: "phone", text: "Teléfono",
            headerStyle: { width: '81px' } },
        { dataField: "therapeuticGroup", text: "Grupo terapéutico", formatter: groupFormatter,
            headerStyle: { width: '120px' } },
        { dataField: "createdAt", text: "Fecha", formatter: dateFormatter, sort: true,
            headerStyle: { width: '110px' } },
        {
            dataField: "block1Done",
            text: "B1",
            formatter: (cell, row) => checkBoxFormatter("block1Done", cell, row),
            headerStyle: { width: "64px", textAlign: "center" },
            align: "center",
            headerAlign: "center"
        },
        {
            dataField: "block2Done",
            text: "B2",
            formatter: (cell, row) => checkBoxFormatter("block2Done", cell, row),
            headerStyle: { width: "64px", textAlign: "center" },
            align: "center",
            headerAlign: "center"
        },
        {
            dataField: "block3Done",
            text: "B3",
            formatter: (cell, row) => checkBoxFormatter("block3Done", cell, row),
            headerStyle: { width: "64px", textAlign: "center" },
            align: "center",
            headerAlign: "center"
        },
        {
            dataField: "extraDone",
            text: "E",
            formatter: (cell, row) => checkBoxFormatter("extraDone", cell, row),
            headerStyle: { width: "40px", textAlign: "center" },
            align: "center",
            headerAlign: "center"
        },
        user?.isAdmin && ({
            dataField: "finalized",
            text: "F",
            formatter: (cell, row) => checkBoxFormatter("finalized", cell, row),
            headerStyle: { width: "40px", textAlign: "center" },
            align: "center",
            headerAlign: "center"
        }),
        {
            dataField: "_id",
            text: "",
            align: 'right',
            formatter: buttonFormatter,
            headerStyle: { width: '220px' }
        },
    ].filter(Boolean);

    const finalizedColumns = [
        { dataField: "fullName", text: "Nombre", sort: true,
            headerStyle: { width: '120px' } },
        { dataField: "surname", text: "Apellidos", sort: true,
            headerStyle: { width: '120px' } },
        { dataField: "phone", text: "Teléfono",
            headerStyle: { width: '81px' } },
        { dataField: "therapeuticGroup", text: "Grupo terapéutico", formatter: groupFormatter,
            headerStyle: { width: '120px' } },
        { dataField: "createdAt", text: "Fecha", formatter: dateFormatter, sort: true,
            headerStyle: { width: '110px' } },
        { dataField: "blockTransitionNotes", text: "Observaciones", formatter: notesFormatter,
            headerStyle: { width: '110px' } },
        {
            dataField: "_id",
            text: "",
            align: 'right',
            formatter: buttonFormatter,
            headerStyle: { width: '150px' }
        },
    ];

    function groupFormatter(cell) {
        if (!cell) return "";
        return cell?.fullName;
    }

    function notesFormatter(cell, row) {
        const count = row?.blockTransitionNotes?.length || 0;
        return count > 0 ? count : "—";
    }

    function isTherapyBlock(block) {
        return block === "block1Done" || block === "block2Done" || block === "block3Done";
    }

    function isAdminBlock(block) {
        return block === "extraDone" || block === "finalized";
    }

    function checkBoxFormatter(block, cell, row) {
        const elem = clients.find((item) => item._id === row._id)
            || oldClients.find((item) => item._id === row._id);

        let disabled = true, defDisable;
        switch (block) {
            case "block1Done" : break;
            case "block2Done" : if (!row?.block1Done) defDisable = true; break;
            case "block3Done" : if (!row?.block2Done) defDisable = true; break;
        }

        if (!defDisable) {
            if (block === "finalized" || block === "extraDone") {
                if (user?.isAdmin) disabled = false;
            }
            else if (!readOnly) {
                let blockNum = null;
                switch (block) {
                    case "block1Done" : blockNum = "block1"; break;
                    case "block2Done" : blockNum = "block2"; break;
                    case "block3Done" : blockNum = "block3"; break;
                }

                const blockTherapist = elem?.therapeuticGroup[blockNum]?.therapist;
                if (getTherapistId(blockTherapist) === user?._id) disabled = false;
            }
        }

        const acronym = getBlockAcronym(elem, block);

        return (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                <input type="checkbox"
                    disabled={disabled}
                    checked={elem?.[block] || false}
                    onChange={() => {
                        if (!elem) return;
                        if (elem[block]) {
                            if (isAdminBlock(block)) {
                                setOpenConfirmDialog(4);
                                setClient(elem);
                                setBlock(block);
                            } else {
                                changeStatusBlockAux(elem, block);
                            }
                        }
                        else if (isTherapyBlock(block)) {
                            setOpenConfirmDialog(3);
                            setClient(elem);
                            setBlock(block);
                            setBlockNote("");
                            const nextBlock = block === "block1Done" ? 2 : block === "block2Done" ? 3 : null;
                            const nextTherapist = nextBlock ? getTherapistForBlock(elem, nextBlock) : null;
                            setNextAppointmentAction("fix");
                            setAppointmentTherapistId(nextTherapist?._id || "");
                            setAppointmentStart("");
                            setAppointmentDuration(60);
                        }
                        else {
                            setOpenConfirmDialog(4);
                            setClient(elem);
                            setBlock(block);
                        }
                    }}
                />
                {acronym && (
                    <span style={{ fontSize: "0.75rem", fontWeight: 600 }}>{acronym}</span>
                )}
            </span>
        )
    }

    function changeStatusBlockAux(client, block, note, extra) {
        const newState = !client[block];
        changeStatusBlockClient(client._id, block, newState, note, extra)
            .then((res) => {
                if (res.status === 200) {
                    alertSuccess({ title: "Éxito!", customMessage: `Bloque ${newState ? "marcado" : "desmarcado"} correctamente.` })
                    fetch();
                    setRefresh(true);
                }
            })
            .catch((error) => {
                alertError({ error: error, customMessage: `No se ha podido cambiar el estado del bloque.` })
            });
    }

    function renderAppointmentButton(elem) {
        if (!canManageAppointments(user, elem)) return null;
        const pending = elem?.nextAppointmentPending;
        return (
            <Tooltip
                title={pending
                    ? `Falta fijar la cita del bloque ${elem.nextAppointmentBlock}`
                    : "Gestionar citas"}
            >
                <Button
                    style={buttonsStyle}
                    size="small"
                    onClick={() => setAppointmentClient(elem)}
                >
                    <EventIcon style={pending ? { color: "#dc3545" } : undefined} />
                </Button>
            </Tooltip>
        );
    }

    function buttonFormatter(cell) {
        const elem = clients.find((item) => item._id === cell) || oldClients.find((item) => item._id === cell);

        if (readOnly || elem?.finalized) return (
        <>
            {!elem?.finalized && renderAppointmentButton(elem)}
            <Tooltip title="Ver">
            <Button
                style={buttonsStyle}
                size="small"
                onClick={() => history.push("/edit-client/" + elem._id)}
            >
                <Visibility />
            </Button>
            </Tooltip>
            {user?.isAdmin && elem?.finalized && (
            <Tooltip title="Borrar">
            <Button
                style={buttonsStyle}
                size="small"
                onClick={() => {
                    setClient(elem);
                    setOpenConfirmDialog(2);
                }}
            >
                <DeleteIcon />
            </Button>
            </Tooltip>
            )}
        </>)
        return (
        <>
            {renderAppointmentButton(elem)}
            <Tooltip title="Editar">
            <Button
                style={buttonsStyle}
                size="small"
                onClick={() => history.push("/edit-client/" + elem._id)}
            >
                <EditIcon />
            </Button>
            </Tooltip>
            {user?.isAdmin && (
            <>
                <Tooltip title={elem?.active ? "Desactivar" : "Activar"}>
                <Button
                    style={buttonsStyle}
                    size="small"
                    onClick={() => {
                        setClient(elem);
                        setOpenConfirmDialog(1);
                    }}
                >
                    {elem?.active ? (
                    <ToggleOffIcon />
                    ) : (
                    <ToggleOnIcon style={{ color: "red" }} />
                    )}
                </Button>
                </Tooltip>
                <Tooltip title="Borrar">
                <Button
                    style={buttonsStyle}
                    size="small"
                    onClick={() => {
                        setClient(elem);
                        setOpenConfirmDialog(2);
                    }}
                >
                    <DeleteIcon />
                </Button>
                </Tooltip>
            </>
            )}
        </>
        );
    }

    return  (
        <Card>
            <CardHeader title="Clientes">
                { user?.isTherapist && (
                    <CardHeaderToolbar>
                    <button
                        type="button"
                        className="btn btn-primary text-white"
                        onClick={() => {
                            if (user?.isTherapist && therapeuticGroups?.length === 0) {
                                let customMessage = "Para añadir nuevos clientes debes ser parte de algún grupo terapéutico.";
                                if (isOnlyTherapist(user)) customMessage += " Por favor, contacta con algún administrador.";
                                alertError({ error: null, customMessage })
                            }
                            else history.push("/edit-client")
                        }}
                    >
                        Añadir
                    </button>
                    </CardHeaderToolbar>
                )}
            </CardHeader>
            <CardBody>
                { !refresh && (
                    <>
                        <Table data={clients} columns={columns} />
                    </>
                )}
            </CardBody>
            {(user?.isAdmin || user?.isTherapist) && (
                <>
                    <CardHeader title="Clientes archivados"></CardHeader>
                    <CardBody>
                        { !refresh && (
                            <>
                                <Table data={oldClients} columns={finalizedColumns} />
                            </>
                        )}
                    </CardBody>
                </>
            )}
            <ConfirmDialog
                title={`Seguro que quieres ${client?.active ? 'deshabilitar' : 'habilitar'} this client?`}
                open={openConfirmDialog === 1}
                setOpen={setOpenConfirmDialog}
                onConfirm={() => {
                    changeStatusClient(client._id, !client?.active)
                        .then((res) => {
                            if (res.status === 200) {
                                alertSuccess({ title: `${client?.active ? 'Deshabilitado!' : 'Habilitado!'}`, customMessage: `Admin ${client?.active ? 'deshabilitado' : 'habilitado'} correctamente` })
                                fetch();
                                setRefresh(true);
                            }
                        })
                        .catch((error) => {
                            alertError({ error: error, customMessage: `No se ha podido ${client?.active ? 'deshabilitar' : 'habilitar'} el cliente.` })
                        });
                }}
            />
            <ConfirmDialog
                title={'Seguro que quieres eliminar el cliente?'}
                open={openConfirmDialog === 2}
                setOpen={setOpenConfirmDialog}
                onConfirm={() => {
                    deleteClient(client?._id)
                        .then((res) => {
                            if (res.status === 204 || res.status === 200) {
                                alertSuccess({
                                    title: 'Eliminado!',
                                    customMessage: 'Client eliminado correctamente.',
                                });
                                fetch();
                                setRefresh(true);
                            }
                        })
                        .catch((error) => {
                            alertError({
                                error: error,
                                customMessage: 'No se ha podido eliminar el client.',
                            });
                        });
                }}
            />
            <Dialog
                open={openConfirmDialog === 3}
                onClose={() => setOpenConfirmDialog(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    ¿Seguro que quieres finalizar el bloque?
                </DialogTitle>
                <DialogContent>
                    <TextField
                        label="Nota para el siguiente terapeuta (opcional)"
                        value={blockNote}
                        onChange={(event) => setBlockNote(event.target.value)}
                        margin="normal"
                        variant="outlined"
                        multiline
                        fullWidth
                        rows={4}
                    />
                    {(block === "block1Done" || block === "block2Done") && (
                        <>
                            <FormControl component="fieldset" margin="normal" fullWidth>
                                <FormLabel component="legend">Siguiente cita</FormLabel>
                                <RadioGroup
                                    value={nextAppointmentAction}
                                    onChange={(event) => setNextAppointmentAction(event.target.value)}
                                >
                                    <FormControlLabel
                                        value="fix"
                                        control={<Radio color="primary" />}
                                        label="Fijar la siguiente cita ahora"
                                    />
                                    <FormControlLabel
                                        value="pending"
                                        control={<Radio color="primary" />}
                                        label="Dejarla pendiente (la fijará un administrador)"
                                    />
                                </RadioGroup>
                            </FormControl>
                            {nextAppointmentAction === "fix" && (
                                <>
                                    <TextField
                                        select
                                        label="Terapeuta"
                                        value={appointmentTherapistId}
                                        onChange={(event) => {
                                            setAppointmentTherapistId(event.target.value);
                                            setAppointmentStart("");
                                        }}
                                        margin="normal"
                                        variant="outlined"
                                        fullWidth
                                    >
                                        {getClientGroupTherapists(client).map((therapist) => (
                                            <MenuItem key={therapist._id} value={therapist._id}>
                                                {therapistLabel(therapist)}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                    <TextField
                                        label="Duración (minutos)"
                                        type="number"
                                        value={appointmentDuration}
                                        onChange={(event) => setAppointmentDuration(event.target.value)}
                                        margin="normal"
                                        variant="outlined"
                                        fullWidth
                                        inputProps={{ min: 15, max: 480, step: 5 }}
                                    />
                                    <AvailabilityGrid
                                        clientId={client?._id}
                                        therapistId={appointmentTherapistId}
                                        durationMinutes={appointmentDuration}
                                        value={appointmentStart}
                                        onChange={setAppointmentStart}
                                    />
                                </>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button color="secondary" onClick={() => setOpenConfirmDialog(false)}>
                        No
                    </Button>
                    <Button
                        color="primary"
                        onClick={() => {
                            const extra = {};
                            if (block === "block1Done" || block === "block2Done") {
                                extra.nextAppointmentAction = nextAppointmentAction;
                                if (nextAppointmentAction === "fix") {
                                    if (!appointmentTherapistId || !appointmentStart) {
                                        alertError({
                                            error: null,
                                            customMessage: "Indica el terapeuta y elige una franja libre.",
                                        });
                                        return;
                                    }
                                    extra.appointment = {
                                        therapistId: appointmentTherapistId,
                                        start: appointmentStart,
                                        durationMinutes: Number(appointmentDuration) || 60,
                                        block: block === "block1Done" ? 2 : 3,
                                    };
                                }
                            }
                            setOpenConfirmDialog(false);
                            changeStatusBlockAux(client, block, blockNote, extra);
                        }}
                    >
                        Sí
                    </Button>
                </DialogActions>
            </Dialog>
            <ConfirmDialog
                title={`Seguro que quieres ${client?.[block] ? 'desmarcar' : 'marcar'} ${block === 'extraDone' ? 'la cita extra' : 'la sesión como finalizada'}?`}
                open={openConfirmDialog === 4}
                setOpen={setOpenConfirmDialog}
                onConfirm={() => {
                    changeStatusBlockAux(client, block);
                }}
            />
            <AppointmentDialog
                open={!!appointmentClient}
                client={appointmentClient}
                onClose={() => {
                    setAppointmentClient(null);
                    fetch();
                    setRefresh(true);
                }}
            />
        </Card>
    )
}
